import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ReportFilters {
  month?: number;
  year?: number;
  searchText?: string;
}

export interface ReportPayload {
  pageIndex: number;
  pageSize: number;
  filters?: ReportFilters;
}

export interface ReportData {
  invoiceDate: string | Date;
  invoiceNumber: string;
  /** Contact person (guardian); not the patient `name`. */
  clientName: string;
  clientRefNo: string;
  clientId: string | null;
  shiftCharges: number;
  additionalCharges: number;
  total: number;
  month: number;
  year: number;
  // Walk-in rows ride this same shape but expose extra context the
  // walk-in report renders. Marked optional so existing reports keep
  // working without changes.
  contactNumber?: string;
  procedures?: string[];
  nurseName?: string;
  doctorName?: string;
  // ProcedureRecord _id for walk-in rows (each row is a single record).
  // Used to deep-link to the walk-in invoice page from the report table.
  procedureId?: string;
  /** True when the linked client record is currently archived. */
  clientArchived?: boolean;
  /** Walk-in / procedure rows: archived charge record (no client document). */
  recordArchived?: boolean;
}

export interface ReportTotals {
  totalShiftCharges: number;
  totalAdditionalCharges: number;
  grandTotal: number;
}

export interface ReportPagination {
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}

export interface ReportResponse {
  success: boolean;
  data: {
    reportData: ReportData[];
    totals: ReportTotals;
    pagination: ReportPagination;
  };
}

export interface RegistrationRenewalPayload {
  reportType: 'Monthly' | 'Annually';
  month?: number;
  year: number;
}

export interface RegistrationRenewalResponse {
  success: boolean;
  data: {
    reportData: any[];
    totals: {
      totalAnnualFee: number;
      totalLifeTimeFee: number;
      grandTotal: number;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get registered clients report with pagination
   * @param payload - { pageIndex, pageSize, filters: { month?, year?, searchText? } }
   * @returns Observable<ReportResponse>
   */
  getRegisteredClientsReport(payload: ReportPayload): Observable<ReportResponse> {
    return this.http.post<ReportResponse>(
      `${this.apiUrl}/reports/registered-clients`,
      payload
    );
  }

  /**
   * Get non-registered clients report with pagination
   * @param payload - { pageIndex, pageSize, filters: { month?, year?, searchText? } }
   * @returns Observable<ReportResponse>
   */
  getNonRegisteredClientsReport(payload: ReportPayload): Observable<ReportResponse> {
    return this.http.post<ReportResponse>(
      `${this.apiUrl}/reports/non-registered-clients`,
      payload
    );
  }

  /**
   * Get registration and renewal income report
   * @param payload - { reportType: 'Monthly' | 'Annually', month?, year }
   * @returns Observable<RegistrationRenewalResponse>
   */
  getRegistrationRenewalIncomeReport(payload: RegistrationRenewalPayload): Observable<RegistrationRenewalResponse> {
    return this.http.post<RegistrationRenewalResponse>(
      `${this.apiUrl}/reports/registration-renewal-income`,
      payload
    );
  }

  /**
   * Get inward clients revenue report with pagination.
   * Mirrors the registered clients report structure.
   */
  getInwardClientsReport(payload: ReportPayload): Observable<ReportResponse> {
    return this.http.post<ReportResponse>(
      `${this.apiUrl}/reports/inward-clients`,
      payload
    );
  }

  /**
   * Walk-in clients revenue report. Returns one row per ProcedureRecord
   * tagged as walk-in (no Client document). Same toolbar/payload shape as
   * the other paginated revenue reports so the UI can be modeled after
   * the non-registered clients report.
   */
  getWalkInClientsReport(payload: ReportPayload): Observable<ReportResponse> {
    return this.http.post<ReportResponse>(
      `${this.apiUrl}/reports/walk-in-clients`,
      payload
    );
  }

  /**
   * Consolidated income summary across every revenue category for a
   * single period. Returns totals only - no row-level data.
   */
  getConsolidatedIncomeReport(
    payload: ConsolidatedIncomePayload,
  ): Observable<ConsolidatedIncomeResponse> {
    return this.http.post<ConsolidatedIncomeResponse>(
      `${this.apiUrl}/reports/consolidated-income`,
      payload,
    );
  }
}

export interface ConsolidatedIncomePayload {
  reportType: 'Monthly' | 'Annually' | 'Custom';
  month?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
}

export interface ConsolidatedIncomeBreakdownItem {
  shiftCharges?: number;
  additionalCharges?: number;
  total: number;
}

export interface ConsolidatedIncomeResponse {
  success: boolean;
  data: {
    window: { start: string; end: string; reportType: string };
    breakdown: {
      registered: ConsolidatedIncomeBreakdownItem;
      nonRegistered: ConsolidatedIncomeBreakdownItem;
      inward: ConsolidatedIncomeBreakdownItem;
      walkIn: ConsolidatedIncomeBreakdownItem;
      registration: { total: number };
      renewal: { total: number };
    };
    totals: {
      registeredRevenue: number;
      nonRegisteredRevenue: number;
      inwardRevenue: number;
      walkInRevenue: number;
      registrationIncome: number;
      renewalIncome: number;
      grandTotal: number;
    };
  };
}
